import { useEffect, useState } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import QRCode from 'qrcode';
import { db, functions } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.jsx';

// AccessPoints — printable QR codes that give anyone instant, signup-free
// access to report a fault (2026-08-29).
//
// Two shapes, same机制:
//   • Site-bound  — "Bondi Junction store", with an address. Tickets arrive
//     already tagged with the location. For bigger customers with fixed sites.
//   • Generic     — "Residents, 12 Smith St". The reporter types where they are.
//     For agencies handing stickers to the people living in a building.
//
// The QR encodes /r/{vendorId}/{apId}?t={token}. Deactivating a code kills every
// sticker printed from it, instantly.

const PUBLIC_BASE = 'https://giraops.com';

export default function AccessPoints() {
  const { claims } = useAuth();
  const vendorId = claims?.vendor_id;

  const [points, setPoints] = useState(null);
  const [skills, setSkills] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [qrFor, setQrFor] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!vendorId) return;
    return onSnapshot(
      query(collection(db, 'vendors', vendorId, 'accessPoints'), orderBy('createdAt', 'desc')),
      (snap) => setPoints(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => setError(err.message),
    );
  }, [vendorId]);

  useEffect(() => {
    if (!vendorId) return;
    return onSnapshot(
      collection(db, 'vendors', vendorId, 'skillCategories'),
      (snap) => setSkills(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      () => {},
    );
  }, [vendorId]);

  async function toggle(ap) {
    try {
      await httpsCallable(functions, 'setAccessPointActive')({
        accessPointId: ap.id, active: !ap.active,
      });
    } catch (e) { setError(e.message); }
  }

  async function remove(ap) {
    if (!confirm(`Delete "${ap.label}"? Any sticker already printed from it will stop working.`)) return;
    try {
      await httpsCallable(functions, 'deleteAccessPoint')({ accessPointId: ap.id });
    } catch (e) { setError(e.message); }
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ margin: 0 }}>QR access</h2>
        <button className="btn-primary" onClick={() => setShowNew(true)}>+ New QR code</button>
      </div>
      <p style={{ color: 'var(--muted)', marginTop: -4, maxWidth: 760 }}>
        Printable codes that let anyone report a fault with <strong>no signup</strong> — they scan,
        type what's wrong, and can then message your technician. Give one to a customer site, or
        hand them out to the residents of a building you look after.
      </p>

      {error && <div className="error">{error}</div>}

      {points === null && <div className="placeholder">Loading…</div>}
      {points && points.length === 0 && (
        <div className="placeholder">
          No QR codes yet. Create one, print it, and stick it where people will be standing when
          something breaks — reception, the plant room, next to the machine.
        </div>
      )}

      {points && points.length > 0 && (
        <table className="data-table">
          <thead>
            <tr>
              <th>Label</th>
              <th>Location</th>
              <th>Scans</th>
              <th>Tickets</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {points.map((ap) => (
              <tr key={ap.id} style={ap.active === false ? { opacity: 0.55 } : undefined}>
                <td><strong>{ap.label}</strong></td>
                <td>
                  {ap.siteName || <span style={{ color: 'var(--muted)' }}>— any</span>}
                  {ap.address && (
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>{ap.address}</div>
                  )}
                </td>
                <td>{ap.scanCount || 0}</td>
                <td>{ap.ticketCount || 0}</td>
                <td>
                  <span className="badge" style={{
                    background: 'transparent',
                    border: `1px solid ${ap.active === false ? '#dc2626' : '#10b981'}`,
                    color: ap.active === false ? '#dc2626' : '#10b981',
                  }}>
                    {ap.active === false ? 'Off' : 'Live'}
                  </span>
                </td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button className="btn-chip" onClick={() => setQrFor(ap)}>QR</button>{' '}
                  <button className="btn-chip" onClick={() => toggle(ap)}>
                    {ap.active === false ? 'Turn on' : 'Turn off'}
                  </button>{' '}
                  <button className="btn-ghost-danger" onClick={() => remove(ap)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showNew && (
        <NewAccessPointModal skills={skills} onClose={() => setShowNew(false)} />
      )}
      {qrFor && (
        <QrModal ap={qrFor} vendorId={vendorId} onClose={() => setQrFor(null)} />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

function NewAccessPointModal({ skills, onClose }) {
  const [label, setLabel] = useState('');
  const [siteName, setSiteName] = useState('');
  const [address, setAddress] = useState('');
  const [skillId, setSkillId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function submit(e) {
    e.preventDefault();
    if (!label.trim() || busy) return;
    setBusy(true); setError(null);
    try {
      await httpsCallable(functions, 'createAccessPoint')({
        label: label.trim(),
        siteName: siteName.trim(),
        address: address.trim(),
        defaultSkillCategoryId: skillId || undefined,
      });
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h3>New QR code</h3>

        <label>Label (for your list)</label>
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Bondi Junction store — or: Residents, 12 Smith St"
          required
        />

        <label>Site name (optional)</label>
        <input
          value={siteName}
          onChange={(e) => setSiteName(e.target.value)}
          placeholder="Leave blank if the reporter should tell you where they are"
        />
        <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: -8, marginBottom: 8 }}>
          Set this for a fixed site and every ticket arrives already tagged with it.
        </div>

        <label>Address to pre-fill (optional)</label>
        <input
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="12 Smith St, Bondi Junction NSW"
        />

        <label>Default problem type (optional)</label>
        <select value={skillId} onChange={(e) => setSkillId(e.target.value)}>
          <option value="">Let the reporter choose</option>
          {skills.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-primary" style={{ flex: 1 }} disabled={busy}>
            {busy ? 'Creating…' : 'Create QR code'}
          </button>
        </div>
        {error && <div className="error">{error}</div>}
      </form>
    </div>
  );
}

function QrModal({ ap, vendorId, onClose }) {
  const [dataUrl, setDataUrl] = useState(null);
  const url = `${PUBLIC_BASE}/r/${vendorId}/${ap.id}?t=${ap.token}`;

  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(url, {
      errorCorrectionLevel: 'H',
      width: 600, margin: 2,
      color: { dark: '#0f172a', light: '#ffffff' },
    }).then((png) => { if (!cancelled) setDataUrl(png); }).catch(() => {});
    return () => { cancelled = true; };
  }, [url]);

  function downloadPng() {
    if (!dataUrl) return;
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `giraops-qr-${slugify(ap.label)}.png`;
    a.click();
  }

  function printSticker() {
    if (!dataUrl) return;
    const w = window.open('', '_blank', 'width=420,height=560');
    if (!w) return;
    w.document.write(`<!DOCTYPE html>
<html><head><title>Giraops QR - ${escapeHtml(ap.label)}</title>
<style>
  body { font-family: sans-serif; margin: 18px; text-align: center; color: #0f172a; }
  .head { font-size: 19px; font-weight: 700; margin-bottom: 2px; }
  .sub { font-size: 13px; color: #475569; margin-bottom: 14px; }
  .qr { display: block; margin: 0 auto 10px; width: 270px; height: 270px; }
  .site { font-size: 14px; font-weight: 600; }
  .addr { font-size: 12px; color: #64748b; }
  .footer { font-size: 11px; color: #94a3b8; margin-top: 14px; }
  @media print { body { margin: 4mm; } }
</style></head><body>
  <div class="head">Something broken?</div>
  <div class="sub">Scan this code to report it — no app, no sign-up.</div>
  <img class="qr" src="${dataUrl}" alt="QR code" />
  ${ap.siteName ? `<div class="site">${escapeHtml(ap.siteName)}</div>` : ''}
  ${ap.address ? `<div class="addr">${escapeHtml(ap.address)}</div>` : ''}
  <div class="footer">24/7 service · powered by Giraops</div>
  <script>window.onload = () => window.print();</script>
</body></html>`);
    w.document.close();
  }

  return (
    <div className="ra-modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="ra-modal" style={{ maxWidth: 420, textAlign: 'center' }}>
        <h3>{ap.label}</h3>
        <p className="settings-help" style={{ marginTop: -6, textAlign: 'left' }}>
          Print this and put it where people will be standing when something breaks.
          Scanning opens a report form immediately — no account needed.
        </p>
        {dataUrl
          ? <img src={dataUrl} alt="QR code" style={{ width: 240, height: 240, margin: '4px auto 10px' }} />
          : <div className="placeholder">Generating…</div>}
        <div style={{
          fontSize: 11, color: 'var(--muted)', wordBreak: 'break-all',
          background: '#f8fafc', padding: 8, borderRadius: 6, textAlign: 'left',
        }}>
          {url}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button className="btn-ghost" style={{ flex: 1 }} onClick={() => navigator.clipboard?.writeText(url)}>
            Copy link
          </button>
          <button className="btn-ghost" style={{ flex: 1 }} onClick={downloadPng}>Download</button>
          <button className="btn-primary" style={{ flex: 1 }} onClick={printSticker}>Print</button>
        </div>
        <button className="btn-ghost" style={{ width: '100%', marginTop: 8 }} onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

function slugify(s) {
  return String(s || 'code').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40);
}
function escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
